// apps/web/src/pages/Public/MagicLink/components/SignatureEmbed.tsx
//
// Dispatcher de embed de assinatura. Documentos criados antes da virada de
// provider continuam na ClickSign (widget JS + re-mint de embed key por 410);
// os novos são ZapSign (iframe puro, link que não expira). Este é o único lugar
// que escolhe entre os dois — a decisão sai de `resolveSignatureProvider`.

import { ClicksignEmbedWidget } from './ClicksignEmbedWidget';
import type { SignatureEmbedSource } from './signatureProvider';
import { resolveSignatureProvider } from './signatureProvider';
import { ZapsignEmbedFrame } from './ZapsignEmbedFrame';

export interface SignatureEmbedProps extends SignatureEmbedSource {
  /** Token do signatário. ClickSign: chave do widget. ZapSign: só informativo. */
  requestSignatureKey?: string | null;
  /** Assinatura concluída no embed — o consumidor reconcilia via polling de status. */
  onSigned: () => void;
  /**
   * Canal de falha do embed ClickSign (chave morta / 410 / timeout de load), que
   * o consumidor usa para re-emitir uma chave fresca. NÃO é acionado no ZapSign:
   * lá o link não expira e o retry é local (recarrega o mesmo iframe).
   */
  onError: (error?: unknown) => void;
  /** Override do teto de espera pelo load do embed (ms). Exposto para testes. */
  loadTimeoutMs?: number;
  /**
   * Abaixo de `md`, o embed estica para a altura do contêiner em vez de usar
   * altura fixa. Exige pai com altura definida. Só o embed ZapSign responde —
   * o widget ClickSign (legado) mantém altura fixa.
   */
  fillHeight?: boolean;
}

export function SignatureEmbed({
  provider,
  envelopeUrl,
  requestSignatureKey,
  onSigned,
  onError,
  loadTimeoutMs,
  fillHeight,
}: SignatureEmbedProps) {
  const resolved = resolveSignatureProvider({ provider, envelopeUrl });

  if (resolved === 'zapsign') {
    // `envelopeUrl` é a própria URL do iframe — sem ela não há o que embutir.
    // Os consumidores só montam o embed quando o status já trouxe a URL.
    if (!envelopeUrl) return null;
    return (
      <ZapsignEmbedFrame
        envelopeUrl={envelopeUrl}
        onSigned={onSigned}
        loadTimeoutMs={loadTimeoutMs}
        fillHeight={fillHeight}
      />
    );
  }

  if (!requestSignatureKey) return null;
  return (
    <ClicksignEmbedWidget
      requestSignatureKey={requestSignatureKey}
      envelopeUrl={envelopeUrl ?? undefined}
      onSigned={onSigned}
      onError={onError}
      loadTimeoutMs={loadTimeoutMs}
    />
  );
}
